// src/pages/dashboard/components/superadmin/dashboardHelpers.ts
import type { CompanyStatistics } from '../../models';

type StatField = keyof Omit<CompanyStatistics, 'day'>;

const numericFields: StatField[] = [
  'users',
  'customers',
  'serviceObjects',
  'orders',
  'completeOrders',
  'plannedInterventions',
  'completeInterventions'
];

export const aggregateByWeek = (data: CompanyStatistics[]): CompanyStatistics[] => {
  if (data.length === 0) return data;
  const weeks: Map<string, CompanyStatistics> = new Map();
  data.forEach((entry) => {
    const d = new Date(entry.day);
    const dayOfWeek = d.getDay() || 7;
    const monday = new Date(d);
    monday.setDate(d.getDate() - dayOfWeek + 1);
    const key = monday.toISOString().slice(0, 10);
    const existing = weeks.get(key);
    if (existing) {
      numericFields.forEach((f) => {
        (existing[f] as number) += entry[f];
      });
    } else {
      weeks.set(key, { day: key, ...Object.fromEntries(numericFields.map((f) => [f, entry[f]])) } as CompanyStatistics);
    }
  });
  return Array.from(weeks.values());
};

export const aggregateByMonth = (data: CompanyStatistics[]): CompanyStatistics[] => {
  if (data.length === 0) return data;
  const months: Map<string, CompanyStatistics> = new Map();
  data.forEach((entry) => {
    const key = entry.day.slice(0, 7);
    const existing = months.get(key);
    if (existing) {
      numericFields.forEach((f) => {
        (existing[f] as number) += entry[f];
      });
    } else {
      months.set(key, { day: key, ...Object.fromEntries(numericFields.map((f) => [f, entry[f]])) } as CompanyStatistics);
    }
  });
  return Array.from(months.values());
};

/** Keep only daily warehouse rows within the [from, to] window (inclusive, ISO yyyy-MM-dd strings). */
export const filterWarehouseRange = (
  data: CompanyStatistics[],
  from: string,
  to: string
): CompanyStatistics[] => data.filter((d) => d.day >= from && d.day <= to);

/** Bucket daily warehouse rows by the dashboard granularity (day / week / month). */
export const aggregateWarehouseByGranularity = (
  data: CompanyStatistics[],
  granularity: 'day' | 'week' | 'month'
): CompanyStatistics[] => {
  if (granularity === 'week') return aggregateByWeek(data);
  if (granularity === 'month') return aggregateByMonth(data);
  return data;
};

/**
 * companyId -> name lookup built from the superadmin companies codebook.
 * The Statistics module returns only companyIds; names are resolved on the frontend.
 */
export const buildCompanyNameMap = (
  companies: { id: string; name: string }[] | undefined
): Map<string, string> => new Map((companies ?? []).map((c) => [c.id, c.name]));

/** Resolve a company name from the codebook map, falling back to an empty string when unknown. */
export const resolveCompanyName = (nameMap: Map<string, string>, companyId: string): string =>
  nameMap.get(companyId) ?? '';
